import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import useIsMobile from "hooks/useIsMobile";

export default function Tooltip({ content, children, className = "" }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimer = useRef();

  // Open on hover (desktop), click (mobile)
  const openTooltip = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  //   const closeTooltip = () => {
  //     clearTimeout(closeTimer.current);
  //     setOpen(false);
  //   };
  const handleMouseEnter = () => {
    if (!isMobile) openTooltip();
  };
  const handleMouseLeave = () => {
    if (!isMobile) {
      // Delay closing to allow moving between trigger and tooltip
      closeTimer.current = setTimeout(() => setOpen(false), 100);
    }
  };
  const handleClick = (e) => {
    if (isMobile) setOpen((prev) => !prev);
  };

  // Calculate position for portal tooltip
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.left + window.scrollX + rect.width / 2,
        width: rect.width,
      });
    }
  }, [open]);

  // Close on outside click or ESC
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  // Tooltip panel rendered in portal to avoid parent overflow clipping
  const tooltipPanel =
    open && typeof window !== "undefined"
      ? ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            id="tooltip"
            className="z-30 min-w-[220px] max-w-xs bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-sm text-black absolute"
            style={{
              top: position.top,
              left: position.left,
              transform: "translateX(-50%)",
            }}
            role="tooltip"
            onMouseEnter={openTooltip}
            onMouseLeave={handleMouseLeave}
          >
            {content}
          </div>,
          document.body
        )
      : null;

  return (
    <div className={"relative inline-block " + className}>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        tabIndex={0}
        aria-describedby={open ? "tooltip" : undefined}
        style={{ cursor: "pointer" }}
      >
        {children}
      </span>
      {tooltipPanel}
    </div>
  );
}
// Tooltip panel is rendered in a portal to avoid clipping by parent overflow/hidden.
// Tooltip stays open when hovering trigger or tooltip panel, closes with short delay otherwise.
